import type { ReactElement } from "react";
import { escapePrintHtml } from "@/lib/printing/document";
import type {
  PaperWidth,
  PrintHardwareActions,
} from "@/lib/printing/types";
import type { Locale } from "@/lib/i18n/config";

export type PrintArtifactKind = "KITCHEN_CHIT" | "CUSTOMER_RECEIPT" | "PRE_BILL_CHECK";
export type PrintArtifactAction =
  | "ORIGINAL"
  | "REPRINT"
  | "CHANGE"
  | "VOID"
  | "REMAKE"
  | "REROUTE";

export type StandalonePrintArtifact = {
  kind: PrintArtifactKind;
  action: PrintArtifactAction;
  paperWidth: PaperWidth;
  /** Complete UTF-8 HTML document for QZ/browser provider adapters. */
  documentHtml: string;
  /**
   * A request only. The provider still refuses raw actions unless the selected
   * named hardware profile explicitly supports them.
   */
  hardwareActions?: PrintHardwareActions;
};

const ROLL_WIDTHS: Record<PaperWidth, { page: number; content: number }> = {
  "58mm": { page: 58, content: 52 },
  "80mm": { page: 80, content: 74 },
};

/**
 * Wrap one template in one standalone print scope.
 *
 * No popup state, queue identity or cash-drawer policy lives here. This layer
 * only renders escaped React markup and carries an explicit hardware request
 * forward for the provider/profile boundary to authorize or reject.
 */
export async function renderStandalonePrintArtifact(input: {
  kind: PrintArtifactKind;
  action?: PrintArtifactAction;
  locale: Locale;
  paperWidth: PaperWidth;
  title: string;
  content: ReactElement;
  hardwareActions?: PrintHardwareActions;
}): Promise<StandalonePrintArtifact> {
  const action = input.action ?? "ORIGINAL";
  const direction = input.locale === "ar" ? "rtl" : "ltr";
  const width = ROLL_WIDTHS[input.paperWidth];
  const title = escapePrintHtml(input.title);
  // Keep the server renderer out of the startup path. Provider documents are
  // built only when a print job is actually prepared.
  const { renderToStaticMarkup } = await import("react-dom/server");
  const markup = renderToStaticMarkup(input.content);
  const documentHtml = [
    "<!doctype html>",
    `<html lang="${input.locale}" dir="${direction}">`,
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    `<title>${title}</title>`,
    "<style>",
    `@page{size:${width.page}mm auto;margin:3mm}`,
    "*{box-sizing:border-box}",
    `html,body{margin:0;padding:0;background:#fff;color:#000;width:${width.content}mm}`,
    'body{font-family:Arial,"Noto Sans Arabic",sans-serif;font-size:11px;line-height:1.35;unicode-bidi:plaintext}',
    ".receipt{display:block!important;position:static!important;inset:auto!important;width:100%!important;max-width:100%;padding:0!important;color:#000;overflow-wrap:anywhere;word-break:break-word}",
    ".receipt table{width:100%;border-collapse:collapse;table-layout:fixed}",
    ".receipt td{overflow-wrap:anywhere;word-break:break-word}",
    ".receipt hr{border:0;border-top:1px solid #000}",
    ".print-artifact-kind{text-align:center;font-size:9px;font-weight:800;letter-spacing:.08em;margin-bottom:1mm}",
    ".print-artifact-marker{border:2px solid #000;padding:1.5mm;margin-bottom:2mm;text-align:center;font-size:15px;font-weight:800;letter-spacing:.04em;overflow-wrap:anywhere}",
    ".print-artifact-marker small{display:block;font-size:10px;font-weight:600;letter-spacing:0}",
    "</style>",
    "</head>",
    `<body data-print-scope="${input.kind}" data-print-active="true" data-paper-width="${input.paperWidth}" data-artifact-action="${action}">`,
    markup,
    "</body></html>",
  ].join("");

  return {
    kind: input.kind,
    action,
    paperWidth: input.paperWidth,
    documentHtml,
    ...(input.hardwareActions ? { hardwareActions: input.hardwareActions } : {}),
  };
}
